import React, { useState, useMemo, useCallback } from 'react';
import {
    Check,
    ChevronDown,
    Copy,
    Download,
    FileText,
    Image as ImageIcon,
    Info,
    ListChecks,
    Sheet,
    Upload,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { LAYOUT_WORDS } from '../../../../common/utils/pattern-name';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

// ============================================================================
// Types
// ============================================================================

interface FieldSpec {
    field: string;
    description: string;
    required: boolean;
    acceptedValues?: string[];
    example: string;
}

interface CsvInstructionsProps {
    familySlug?: string;
}

interface StepConfig {
    number: number;
    title: string;
    description: string;
    icon: React.ReactNode;
}

// ============================================================================
// Data helpers
// ============================================================================

function getFieldSpecs(familySlug: string, t: TFunction): FieldSpec[] {
    const sharedFields: FieldSpec[] = [
        { field: 'name', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_NAME'), required: true, example: 'Product name' },
        { field: 'description', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_DESCRIPTION'), required: true, example: 'Product description' },
        { field: 'collection', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_COLLECTION'), required: false, example: 'collection1;collection2' },
    ];

    const colorField: FieldSpec = { field: 'color', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_COLOR'), required: true, example: 'Light Yellow' };
    const skuField: FieldSpec = { field: 'sku', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_SKU'), required: true, example: 'BK78568' };
    const linkFields: FieldSpec[] = [
        { field: 'link', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_LINK'), required: true, example: 'https://ardealy.com' },
        { field: 'add_to_cart_link', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_ADD_TO_CART_LINK'), required: false, example: 'https://ardealy.com' },
    ];
    const visibleField: FieldSpec = { field: 'is_visible', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_IS_VISIBLE'), required: true, acceptedValues: ['True', 'False'], example: 'True' };
    // What each row (variant) sells for — optional, and never guessed when a cell is missing.
    const priceFields: FieldSpec[] = [
        { field: 'price', description: t('PRODUCT_PRICING.CSV.FIELD_PRICE', 'Selling price of this variant. Decimals with a dot or a comma (24.90 or 24,90).'), required: false, example: '24,90' },
        { field: 'currency', description: t('PRODUCT_PRICING.CSV.FIELD_CURRENCY', 'Currency of the price, as a code. A price without a currency is not imported.'), required: false, acceptedValues: ['EUR', 'GBP', 'USD', 'CHF', 'CAD', 'AUD', 'MAD'], example: 'EUR' },
        { field: 'price_unit', description: t('PRODUCT_PRICING.CSV.FIELD_PRICE_UNIT', 'What the price is per. Left out, nobody has said, and shoppers are told so.'), required: false, acceptedValues: ['m2', 'box', 'piece', 'unit', 'linear_m', 'roll'], example: 'm2' },
    ];
    const packFields: FieldSpec[] = [
        { field: 'm2_per_box', description: t('PRODUCT_PRICING.CSV.FIELD_M2_PER_BOX', 'Square metres one box covers, so shoppers know how many boxes to buy.'), required: false, example: '1,44' },
        { field: 'pieces_per_box', description: t('PRODUCT_PRICING.CSV.FIELD_PIECES_PER_BOX', 'Number of pieces in one box.'), required: false, example: '8' },
    ];
    const sampleFields: FieldSpec[] = [
        { field: 'sample_available', description: t('PRODUCT_PRICING.CSV.FIELD_SAMPLE_AVAILABLE', 'Whether shoppers can order a sample.'), required: false, acceptedValues: ['true', 'false'], example: 'true' },
        { field: 'sample_price', description: t('PRODUCT_PRICING.CSV.FIELD_SAMPLE_PRICE', 'What a sample costs, in the same currency. 0 or "free" means free.'), required: false, example: '0' },
    ];
    const filesField: FieldSpec = { field: 'files', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_FILES'), required: true, example: '110111766101204.jpg' };

    if (familySlug === 'surface-products') {
        return [
            ...sharedFields,
            { field: 'application', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_APPLICATION'), required: true, acceptedValues: ['Floor', 'Walls'], example: 'Floor' },
            { field: 'type', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_TYPE'), required: true, acceptedValues: ['Tile', 'Hardwood', 'Wallpaper'], example: 'Hardwood' },
            { field: 'size', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_SIZE'), required: true, example: '10x10' },
            colorField,
            { field: 'layout', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_LAYOUT'), required: true, acceptedValues: ['bricks', 'herringbone', 'stackbond', 'stackbond-2x2', 'hexagonal', 'fishscale', 'arabesque', 'alure', 'arrow', 'penny', 'triangle', 'wave', 'longhex', 'flagstone', 'flagstonetight', 'alternating', 'chevron', 'opus', 'cabochon'], example: 'stackbond' },
            skuField,
            { field: 'grout_color', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_GROUT_COLOR'), required: false, example: '#00000000' },
            { field: 'grout_size', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_GROUT_SIZE'), required: false, example: '0' },
            { field: 'grout_editable', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_GROUT_EDITABLE', 'Shoppers can change the grout in the visualizer'), required: false, acceptedValues: ['true', 'false'], example: 'true' },
            { field: 'finish', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_FINISH', 'How it reflects light. Shoppers can turn reflections on for any finish but matte. Empty means matte.'), required: false, acceptedValues: ['matte', 'satin', 'glossy', 'lacquered', 'handmade'], example: 'glossy' },
            ...linkFields, visibleField, filesField,
            ...priceFields, ...packFields, ...sampleFields,
        ];
    }
    if (familySlug === 'furniture') {
        return [
            ...sharedFields,
            { field: 'category', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_CATEGORY', 'Product category'), required: true, acceptedValues: ['Sofa', 'Chair', 'Table', 'Bed', 'Storage', 'Desk', 'Shelf', 'Ottoman', 'Bench', 'Console'], example: 'Sofa' },
            { field: 'material', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_MATERIAL', 'Material or fabric variant axis'), required: false, example: 'Walnut Wood' },
            { field: 'size', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_SIZE_3D', 'Dimensions as WxHxD (width x height x depth in cm)'), required: false, example: '120x80x45' },
            colorField, skuField, ...linkFields, visibleField, filesField,
            { field: 'model_file', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_MODEL_FILE', '3D model filename (.glb or .gltf)'), required: false, example: 'sofa_charcoal.glb' },
            ...priceFields, ...sampleFields,
        ];
    }
    if (familySlug === 'lighting') {
        return [
            ...sharedFields,
            { field: 'category', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_CATEGORY', 'Product category'), required: true, acceptedValues: ['Ceiling Light', 'Pendant', 'Chandelier', 'Floor Lamp', 'Table Lamp', 'Wall Sconce', 'Track Lighting', 'Recessed Light'], example: 'Pendant' },
            { field: 'size', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_SIZE_2D', 'Dimensions as WxH (width x height in cm)'), required: false, example: '40x40' },
            colorField, skuField, ...linkFields, visibleField, filesField,
            { field: 'model_file', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_MODEL_FILE', '3D model filename (.glb or .gltf)'), required: false, example: 'pendant_brass.glb' },
            ...priceFields, ...sampleFields,
        ];
    }
    // Decor
    return [
        ...sharedFields,
        { field: 'category', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_CATEGORY', 'Product category'), required: true, acceptedValues: ['Rug', 'Artwork', 'Mirror', 'Vase', 'Sculpture', 'Cushion', 'Throw', 'Curtain', 'Plant Pot', 'Clock'], example: 'Vase' },
        { field: 'size', description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.FIELD_SIZE_2D', 'Dimensions as WxH (width x height in cm)'), required: false, example: '200x300' },
        colorField, skuField, ...linkFields, visibleField, filesField,
        ...priceFields, ...sampleFields,
    ];
}

/** Patterns grouped by the words they accept after a colon ("stackbond:mirror"). */
function layoutWordGroups(): Array<{ patterns: string[]; words: string[] }> {
    const groups = new Map<string, { patterns: string[]; words: string[] }>();
    for (const [pattern, words] of Object.entries(LAYOUT_WORDS)) {
        const key = words.join(',');
        const group = groups.get(key) ?? { patterns: [], words };
        group.patterns.push(pattern);
        groups.set(key, group);
    }
    return Array.from(groups.values());
}

/** One line of the layout-options tip: the patterns, then each word and what it does. */
const LayoutWordsLine: React.FC<{ patterns: string[]; words: string[]; t: TFunction }> = ({ patterns, words, t }) => (
    <li>
        {patterns.map((p, i) => (
            <React.Fragment key={p}>{i > 0 && ', '}<code>{p}</code></React.Fragment>
        ))}
        {' — '}
        {words.map((w, i) => (
            <React.Fragment key={w}>
                {i > 0 && '; '}
                <code>:{w}</code> {t(`PAGES.IMPORT_PRODUCTS.CSV_GUIDE.LAYOUT_WORD_${w.toUpperCase()}`)}
            </React.Fragment>
        ))}
    </li>
);

function getExampleCsv(familySlug: string): { header: string; rows: string[]; filename: string } {
    if (familySlug === 'surface-products') {
        return {
            header: 'name,description,collection,application,type,size,color,layout,sku,grout_color,grout_size,link,add_to_cart_link,is_visible,files,price,currency,price_unit,m2_per_box,pieces_per_box,sample_available,sample_price',
            rows: [
                'Oak Classic,Natural oak flooring,premium,Floor,Hardwood,20x120,Natural Oak,bricks,OAK-001,#00000000,0,https://store.com/oak,https://store.com/cart/oak,True,oak_natural_01.jpg;oak_natural_02.jpg,45.90,EUR,m2,1.92,8,true,0',
                'Oak Classic,Natural oak flooring,premium,Floor,Hardwood,20x120,Warm Brown,herringbone,OAK-002,#C8B49A,1,https://store.com/oak,https://store.com/cart/oak,True,oak_brown_01.jpg,49.90,EUR,m2,1.92,8,true,0',
                'Marble Square,Polished marble tile,luxury,Floor;Walls,Tile,60x60,White Carrara,stackbond:mirror,MRB-001,#DDDDDD,2,https://store.com/marble,,True,marble_white.jpg,89,EUR,m2,1.08,3,true,5',
            ],
            filename: 'import-surface-example.csv',
        };
    }
    if (familySlug === 'furniture') {
        return {
            header: 'name,description,collection,category,material,size,color,sku,link,add_to_cart_link,is_visible,files,model_file,price,currency',
            rows: [
                'Nordic Sofa,Scandinavian 3-seater,living-room,Sofa,Linen Fabric,220x95x82,Light Grey,SOFA-001,https://store.com/sofa,,True,sofa_grey.jpg,sofa_grey.glb,1290,EUR',
                'Nordic Sofa,Scandinavian 3-seater,living-room,Sofa,Linen Fabric,220x95x82,Navy Blue,SOFA-002,https://store.com/sofa,,True,sofa_navy.jpg,sofa_navy.glb,1290,EUR',
                'Dining Chair,Modern wooden chair,dining,Chair,Walnut Wood,52x48x80,Natural,CHAIR-001,https://store.com/chair,,True,chair_walnut.jpg,chair_walnut.glb,189,EUR',
            ],
            filename: 'import-furniture-example.csv',
        };
    }
    if (familySlug === 'lighting') {
        return {
            header: 'name,description,collection,category,size,color,sku,link,add_to_cart_link,is_visible,files,model_file,price,currency',
            rows: [
                'Arc Pendant,Minimalist pendant light,modern,Pendant,40x40,Matte Black,PEND-001,https://store.com/pendant,,True,pendant_black.jpg,pendant_black.glb,149,EUR',
                'Arc Pendant,Minimalist pendant light,modern,Pendant,60x60,Brass,PEND-002,https://store.com/pendant,,True,pendant_brass.jpg,pendant_brass.glb,189,EUR',
            ],
            filename: 'import-lighting-example.csv',
        };
    }
    return {
        header: 'name,description,collection,category,size,color,sku,link,add_to_cart_link,is_visible,files,price,currency',
        rows: [
            'Geometric Vase,Hand-crafted ceramic,artisan,Vase,15x15x35,Matte White,VASE-001,https://store.com/vase,,True,vase_white.jpg,39.90,EUR',
            'Woven Rug,Handwoven wool rug,bohemian,Rug,200x300,Terracotta,RUG-001,https://store.com/rug,,True,rug_terracotta.jpg,420,EUR',
        ],
        filename: 'import-decor-example.csv',
    };
}

function getSteps(familySlug: string, t: TFunction): StepConfig[] {
    const hasModel = familySlug === 'furniture' || familySlug === 'lighting';
    const isSurface = familySlug === 'surface-products';

    return [
        {
            number: 1,
            title: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_1_TITLE', 'Prepare your CSV'),
            description: isSurface
                ? t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_1_DESC_SURFACE', 'Include product name, dimensions (WxH), color, pattern layout, and image filenames. Use semicolons to separate multiple values.')
                : hasModel
                    ? t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_1_DESC_MODEL', 'Include product name, category, color, dimensions (WxHxD), image filenames, and optional 3D model filenames (.glb/.gltf).')
                    : t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_1_DESC_IMAGE', 'Include product name, category, color, dimensions (WxH or WxHxD), and image filenames. Use semicolons to separate multiple values.'),
            icon: <Sheet className="size-4" />,
        },
        {
            number: 2,
            title: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_2_TITLE', 'Name your files'),
            description: isSurface
                ? t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_2_DESC_SURFACE', 'Make sure the filenames in the CSV files column exactly match the actual image files you will upload. Multiple plank images per variant are supported.')
                : t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_2_DESC_OTHER', 'Make sure the filenames in the CSV match the actual files you will upload. Each product needs at least one image.'),
            icon: <ImageIcon className="size-4" />,
        },
        {
            number: 3,
            title: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_3_TITLE', 'Upload & match'),
            description: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_3_DESC', 'Upload the CSV first, then drag & drop your image files. The system will automatically match files to products by filename.'),
            icon: <Upload className="size-4" />,
        },
    ];
}

function getFamilyLabel(familySlug: string): string {
    switch (familySlug) {
        case 'surface-products': return 'Surface Products';
        case 'furniture': return 'Furniture';
        case 'lighting': return 'Lighting';
        case 'decor': return 'Decor';
        default: return 'Products';
    }
}

// ============================================================================
// Component
// ============================================================================

/** A grey line glyph in a square — the tile used for every icon on this card. */
const IconTile = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <span className={cn('flex size-8 shrink-0 items-center justify-center border bg-muted text-muted-foreground', className)}>
        {children}
    </span>
);

/** Monospace inline code — repeated across the field table and the tips list. */
const codeClass = 'bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground';

export const CsvInstructions: React.FC<CsvInstructionsProps> = ({ familySlug }) => {
    const [expanded, setExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState<'guide' | 'fields' | 'preview'>('guide');
    const [copiedRow, setCopiedRow] = useState<number | null>(null);
    const { t } = useTranslation();

    const effectiveSlug = familySlug || 'surface-products';
    const fieldSpecs = useMemo(() => getFieldSpecs(effectiveSlug, t), [effectiveSlug, t]);
    const exampleCsv = useMemo(() => getExampleCsv(effectiveSlug), [effectiveSlug]);
    const steps = useMemo(() => getSteps(effectiveSlug, t), [effectiveSlug, t]);

    const handleDownloadExample = useCallback(() => {
        const csvContent = [exampleCsv.header, ...exampleCsv.rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.setAttribute('href', URL.createObjectURL(blob));
        link.setAttribute('download', exampleCsv.filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [exampleCsv]);

    const handleCopyRow = useCallback((rowIndex: number) => {
        const row = exampleCsv.rows[rowIndex];
        if (row) {
            navigator.clipboard.writeText(exampleCsv.header + '\n' + row);
            setCopiedRow(rowIndex);
            setTimeout(() => setCopiedRow(null), 2000);
        }
    }, [exampleCsv]);

    const csvColumns = exampleCsv.header.split(',');

    return (
        <Card className="mb-6 gap-0 overflow-hidden py-0">
            <Collapsible open={expanded} onOpenChange={setExpanded}>
                {/* ─── Header ─── */}
                <div
                    onClick={() => setExpanded(!expanded)}
                    className={cn(
                        'flex cursor-pointer items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/50',
                        expanded && 'border-b',
                    )}
                >
                    <div className="flex min-w-0 items-center gap-3">
                        <IconTile><FileText className="size-4" /></IconTile>
                        <div className="min-w-0">
                            <p className="font-heading text-sm font-medium tracking-tight text-foreground">
                                {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TITLE')}
                            </p>
                            {!expanded && (
                                <p className="truncate text-xs text-muted-foreground">
                                    {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.SUBTITLE', 'Learn how to format your CSV for {{family}} import').replace('{{family}}', getFamilyLabel(effectiveSlug))}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        <Button
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleDownloadExample(); }}
                        >
                            <Download />
                            {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.EXAMPLE_BUTTON')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            aria-expanded={expanded}
                            aria-label={
                                expanded
                                    ? (t('COMMON.COLLAPSE', 'Collapse') as string)
                                    : (t('COMMON.EXPAND', 'Expand') as string)
                            }
                        >
                            <ChevronDown className={cn('transition-transform', expanded && 'rotate-180')} />
                        </Button>
                    </div>
                </div>

                {/* ─── Content ─── */}
                <CollapsibleContent>
                    <div className="p-5">
                        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
                            <TabsList>
                                <TabsTrigger value="guide">
                                    <ListChecks />
                                    {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TAB_GUIDE', 'How it works')}
                                </TabsTrigger>
                                <TabsTrigger value="preview">
                                    <Sheet />
                                    {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TAB_PREVIEW', 'CSV Preview')}
                                </TabsTrigger>
                                <TabsTrigger value="fields">
                                    <FileText />
                                    {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TAB_FIELDS', 'Field Reference')}
                                </TabsTrigger>
                            </TabsList>

                            {/* ═══ TAB: How it works ═══ */}
                            <TabsContent value="guide" className="mt-4">
                                {/* Steps */}
                                <ol className="mb-4 grid gap-3 md:grid-cols-3">
                                    {steps.map((step) => (
                                        <li key={step.number} className="border p-4">
                                            <div className="mb-3 flex items-center gap-3">
                                                <IconTile>{step.icon}</IconTile>
                                                <div className="min-w-0">
                                                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                        {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.STEP_LABEL', 'Step {{n}}').replace('{{n}}', String(step.number))}
                                                    </p>
                                                    <p className="text-sm font-medium text-foreground">{step.title}</p>
                                                </div>
                                            </div>
                                            <p className="text-xs leading-relaxed text-muted-foreground">{step.description}</p>
                                        </li>
                                    ))}
                                </ol>

                                {/* Quick Tips */}
                                <div className="border bg-muted/40 p-4">
                                    <div className="mb-2 flex items-center gap-2">
                                        <Check className="size-3.5 text-foreground" />
                                        <p className="text-sm font-medium text-foreground">
                                            {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIPS_TITLE')}
                                        </p>
                                    </div>
                                    <ul className="list-disc space-y-1 ps-5 text-xs leading-relaxed text-muted-foreground [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs [&_code]:text-foreground">
                                        <li>{t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_1')}</li>
                                        <li dangerouslySetInnerHTML={{ __html: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_2').replace('collection', '<code>collection</code>').replace('files', '<code>files</code>') }} />
                                        <li dangerouslySetInnerHTML={{ __html: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_3').replace('True', '<code>True</code>').replace('False', '<code>False</code>') }} />
                                        <li data-testid="csv-price-tip">{t('PRODUCT_PRICING.CSV.QUICK_TIP', 'Prices are per row, so each variant can have its own. A price is only imported with its currency; nothing is guessed.')}</li>
                                        {(effectiveSlug === 'furniture' || effectiveSlug === 'lighting') && (
                                            <li>{t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_3D_MODEL', '3D model files should be in .glb or .gltf format')}</li>
                                        )}
                                        {effectiveSlug !== 'surface-products' && (
                                            <li>{t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_SIZE_DIMS', 'Size must be in WxH or WxHxD format (e.g. 120x80 or 220x95x82) — values are in cm')}</li>
                                        )}
                                        {effectiveSlug === 'surface-products' && (
                                            <li>
                                                <span dangerouslySetInnerHTML={{ __html: t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_LAYOUT_OPTIONS')
                                                    .replace('stackbond:mirror', '<code>stackbond:mirror</code>')
                                                    .replace('bricks:third', '<code>bricks:third</code>') }} />
                                                <ul className="mt-1 list-[circle] space-y-0.5 ps-4">
                                                    {layoutWordGroups().map((g) => (
                                                        <LayoutWordsLine key={g.words.join(',')} patterns={g.patterns} words={g.words} t={t} />
                                                    ))}
                                                </ul>
                                            </li>
                                        )}
                                        {effectiveSlug === 'surface-products' && (
                                            <li>
                                                {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_SETS')}
                                                <ul className="mt-1 list-[circle] space-y-0.5 ps-4">
                                                    <li><code>cabochon</code> — {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_SETS_CABOCHON')}</li>
                                                    <li><code>opus</code> — {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.QUICK_TIP_SETS_OPUS')}</li>
                                                </ul>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </TabsContent>

                            {/* ═══ TAB: CSV Preview ═══ */}
                            <TabsContent value="preview" className="mt-4">
                                <div className="mb-3 border">
                                    {/* Preview header */}
                                    <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2">
                                        <div className="flex min-w-0 items-center gap-2">
                                            <Sheet className="size-4 shrink-0 text-muted-foreground" />
                                            <p className="truncate font-mono text-xs text-foreground">{exampleCsv.filename}</p>
                                            <Badge variant="outline">
                                                {`${exampleCsv.rows.length} ${exampleCsv.rows.length === 1 ? 'row' : 'rows'}`}
                                            </Badge>
                                        </div>
                                        <Button variant="ghost" size="sm" onClick={handleDownloadExample}>
                                            <Download />
                                            {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.DOWNLOAD_CSV', 'Download')}
                                        </Button>
                                    </div>

                                    {/* Spreadsheet */}
                                    <Table style={{ minWidth: csvColumns.length * 120 }}>
                                        <TableHeader>
                                            <TableRow className="bg-muted/40">
                                                <TableHead className="w-8 border-e px-2 font-mono text-[10px] text-muted-foreground" />
                                                {csvColumns.map((col, i) => (
                                                    <TableHead key={i} className="px-3 font-mono text-xs text-foreground">
                                                        {col}
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {exampleCsv.rows.map((row, rowIdx) => {
                                                const cells = row.split(',');
                                                return (
                                                    <TableRow key={rowIdx} className="group">
                                                        <TableCell className="relative w-8 border-e bg-muted/40 px-2 text-center font-mono text-[10px] text-muted-foreground">
                                                            {rowIdx + 1}
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon-xs"
                                                                        aria-label={(copiedRow === rowIdx ? t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.COPIED', 'Copied!') : t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.COPY_ROW', 'Copy row')) as string}
                                                                        onClick={() => handleCopyRow(rowIdx)}
                                                                        className="absolute inset-y-0 end-0 my-auto opacity-0 transition-opacity group-hover:opacity-100"
                                                                    >
                                                                        {copiedRow === rowIdx ? <Check /> : <Copy />}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="left">
                                                                    {copiedRow === rowIdx
                                                                        ? t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.COPIED', 'Copied!')
                                                                        : t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.COPY_ROW', 'Copy row')}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TableCell>
                                                        {csvColumns.map((_, colIdx) => (
                                                            <TableCell
                                                                key={colIdx}
                                                                className="max-w-[180px] overflow-hidden text-ellipsis px-3 font-mono text-[11px] text-muted-foreground"
                                                            >
                                                                {cells[colIdx] || ''}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* Row explanation */}
                                <div className="flex items-start gap-2 border bg-muted/40 p-3">
                                    <Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                                    <p className="text-xs leading-relaxed text-muted-foreground">
                                        {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.PREVIEW_HINT', 'Rows with the same product name are grouped as variants of one product. Each row becomes a separate variant with its own color, size, and images. Download this example and modify it with your own data.')}
                                    </p>
                                </div>
                            </TabsContent>

                            {/* ═══ TAB: Field Reference ═══ */}
                            <TabsContent value="fields" className="mt-4">
                                <div className="border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-muted/40">
                                                {[
                                                    t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TABLE_FIELD_NAME'),
                                                    t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TABLE_DESCRIPTION'),
                                                    t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TABLE_REQUIRED'),
                                                    t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TABLE_ACCEPTED_VALUES'),
                                                    t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.TABLE_EXAMPLE'),
                                                ].map((header) => (
                                                    <TableHead key={header} className="px-3">{header}</TableHead>
                                                ))}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {fieldSpecs.map((spec) => (
                                                <TableRow key={spec.field}>
                                                    <TableCell className="px-3">
                                                        <code className={codeClass}>{spec.field}</code>
                                                    </TableCell>
                                                    <TableCell className="whitespace-normal px-3 text-muted-foreground">
                                                        {spec.description}
                                                    </TableCell>
                                                    <TableCell className="px-3">
                                                        <Badge variant={spec.required ? 'destructive' : 'secondary'}>
                                                            {spec.required
                                                                ? t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.REQUIRED')
                                                                : t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.OPTIONAL')}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="whitespace-normal px-3">
                                                        {spec.acceptedValues ? (
                                                            <div className="flex flex-wrap gap-1">
                                                                {spec.acceptedValues.map((value) => (
                                                                    <Badge key={value} variant="outline" className="font-mono">
                                                                        {value}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="italic text-muted-foreground">
                                                                {t('PAGES.IMPORT_PRODUCTS.CSV_GUIDE.ANY_VALUE')}
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="px-3">
                                                        <code className={codeClass}>{spec.example}</code>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
};
